import React, { FC } from 'react';
import { DialogDefault } from 'DesignSystem';
import { FieldWrapper, ThemeButton } from 'DesignSystem';
import { Form, Formik, Field } from 'formik';
import { Grid, Box } from '@mui/material';
import { PartnerDenyRequestSchema } from 'FeatureModules/Partner/schemas/partners.schema';
import { ReactComponent as UnknownDocument } from 'assets/img/icons/unknown-document.svg';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import useMessages from 'hooks/useMessages';
import Loader from 'Components/Forms/Loader';
import { GET_PARTNER_REQUEST } from 'graphql/secure/queries';
import { useQuery } from '@apollo/client';
import { IGetPartnerRequestData } from 'graphql/secure/queries/partner.queries';
import RequestTitle from '../RequestTitle';
import ReceivedRequestCard from 'FeatureModules/Home/Components/Requests/ReceivedRequestCard';
import { IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import { removeRequestFromCachePartnerHome } from 'FeatureModules/Partner/utils/partner.utils';
import HTMLEditorField from 'DesignSystem/Formik/HTMLEditorField/HTMLEditorField';
import { IDefaultDialogProps } from 'types/dialog.types';

interface IFormInputs {
  requestMessage: string;
}

interface IDenyRequestDialogProps extends IDefaultDialogProps {
  requestId: string | undefined | null;
}

const DenyRequestDialog: FC<IDenyRequestDialogProps> = ({ open, onClose, requestId }) => {
  const { updatePartnerRequest, loading } = usePartnerRequestActions();
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { data, loading: loadingRequest } = useQuery<IGetPartnerRequestData>(GET_PARTNER_REQUEST, {
    variables: {
      id: requestId,
    },
  });

  const handleDismissRequest = (values: IFormInputs) => {
    updatePartnerRequest({
      variables: {
        id: requestId as string,
        input: {
          requestStatus: 'DENIED',
          requestReplyMessage: values.requestMessage,
        },
      },

      onError: () =>
        setErrorMessage('Something went wrong dismissing the request. Please try again later.'),
      onCompleted: () => {
        setSuccessMessage('You have dismissed the request');
        onClose?.();
      },
      update: (cache, { data }) => {
        if (data) {
          removeRequestFromCachePartnerHome(cache, data.updatePartnerRequest.partnerRequest);
        }
      },
    });
  };

  return (
    <DialogDefault
      open={!!open}
      title="Dismiss request"
      onClose={onClose}
      maxWidth="sm"
      icon={UnknownDocument}
      data-testid="dismiss-request-dialog"
    >
      <Box px={3} py={1} mb={2} minWidth={480} minHeight={300}>
        {loadingRequest && !data ? (
          <Loader />
        ) : (
          <Box>
            <Box mb={3}>
              <RequestTitle request={data?.partnerRequest as IPartnerRequest} />
            </Box>
            <Box mb={3}>
              <ReceivedRequestCard
                request={data?.partnerRequest as IPartnerRequest}
                displayActions={false}
              />
            </Box>
            <Formik
              initialValues={PartnerDenyRequestSchema.default() as IFormInputs}
              validateOnBlur={false}
              validateOnChange={true}
              validationSchema={PartnerDenyRequestSchema}
              onSubmit={handleDismissRequest}
            >
              {({ values, isValid }) => (
                <Form>
                  <Grid container direction="column" spacing={3}>
                    <Grid item xs={12}>
                      <FieldWrapper label="Describe why you cannot fulfill this request">
                        <Field
                          component={HTMLEditorField}
                          name="requestMessage"
                          minHeight={122}
                          placeholder="Write your message..."
                          data-testid="request-form-message"
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between">
                        <ThemeButton size="medium" onClick={onClose} color="BLUE_ICE">
                          Cancel
                        </ThemeButton>
                        <ThemeButton
                          loading={loading}
                          disabled={!values['requestMessage'] || !isValid}
                          size="medium"
                          color="PRIMARY"
                          type="submit"
                          data-testid="dismiss-send-btn"
                        >
                          Dismiss & Send message
                        </ThemeButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        )}
      </Box>
    </DialogDefault>
  );
};

export default DenyRequestDialog;
